import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { SignInPageContext } from "@insite/content-library/Pages/SignInPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    content = "content",
    guestContent = "guestContent",
}

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const accountSettings = getSettingsCollection(state).accountSettings;
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");

    return {
        allowCreateAccount: accountSettings.allowCreateAccount,
        allowGuestCheckout: accountSettings.allowGuestCheckout && !getCurrentUserIsGuest(state) && referredFromShipping,
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.content]: string;
        [fields.guestContent]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    signInCreateNewAccountTitle: {
        variant: "h4",
    },
    signInCreateNewAccountTitleGridItem: {
        width: 12,
        css: css`
             {
                padding-bottom: 0px;
            }
        `,
    },
    signInCreateNewAccountTextGridItem: {
        width: 12,
        css: css`
             {
                padding-bottom: 0px;
                padding-top: 0px;
            }
        `,
    },
    signInCreateNewAccountButtonGridItem: {
        width: 12,
        css: css`
             {
                margin-top: 10px !important;
            }
            .Guest-checkout-btn-container {
                width: 100%;
                text-align: left;
            }
            .Create-account-btn-container {
                width: 100%;
                text-align: right;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            // flex-direction: row;
                            display: block;
                            button {
                                width: 100%;
                            }
                        `,
                        null,
                        null,
                        css`
                            flex-direction: row;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    checkoutAsGuestButton: {
        variant: "tertiary",
        css: css`
            &.buttonTransition {
                background: linear-gradient(to left, #8ab994 50%, #58b46c 50%);
                background-size: 200% 100%;
                background-position: left bottom;
                animation: gradient-animation 9s ease-in-out infinite;
                @keyframes gradient-animation {
                    0% {
                        background-position: right bottom;
                    }
                    100% {
                        background-position: left top;
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            margin-bottom: 1rem;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    createNewAccountButton: {
        variant: "secondary",
    },
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields,
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);

    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!returnUrl) {
            return;
        }
        setIsButtonDisabled(true);

        const button = event.currentTarget;
        button.classList.add("buttonTransition");

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    return allowGuestCheckout ? (
        <GridContainer {...styles.signInCreateNewAccountGridContainer}>
            <GridItem {...styles.signInCreateNewAccountTitleGridItem}>
                <Typography {...styles.signInCreateNewAccountTitle}>{translate("Guest Checkout")}</Typography>
            </GridItem>

            <GridItem {...styles.signInCreateNewAccountTextGridItem}>
                <Typography {...styles.signInCreateNewAccountText}>
                    {parse(fields.guestContent, parserOptions)}
                </Typography>
            </GridItem>
            <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                <StyledWrapper className="Guest-checkout-btn-container">
                    <Button
                        {...styles.checkoutAsGuestButton}
                        onClick={handleCheckoutAsGuest}
                        data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                        disabled={isButtonDisabled}
                    >
                        {translate("Checkout as Guest")}
                    </Button>
                </StyledWrapper>

                {createAccountUrl && (
                    <StyledWrapper className="Create-account-btn-container">
                        <Button
                            {...styles.createNewAccountButton}
                            onClick={handleCreateNewAccount}
                            data-test-selector="signInCreateNewAccount_createNewAccount"
                        >
                            {translate("Create Account")}
                        </Button>
                    </StyledWrapper>
                )}
            </GridItem>
        </GridContainer>
    ) : (
        <GridContainer {...styles.signInCreateNewAccountGridContainer}>
            <GridItem {...styles.signInCreateNewAccountTitleGridItem}>
                <Typography {...styles.signInCreateNewAccountTitle}>{translate("Create an Account")}</Typography>
            </GridItem>

            <GridItem {...styles.signInCreateNewAccountTextGridItem}>
                <Typography {...styles.signInCreateNewAccountText}>{parse(fields.content, parserOptions)}</Typography>
            </GridItem>

            <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                {createAccountUrl && (
                    <StyledWrapper className="Create-account-btn-container">
                        <Button
                            {...styles.createNewAccountButton}
                            onClick={handleCreateNewAccount}
                            data-test-selector="signInCreateNewAccount_createNewAccount"
                        >
                            {translate("Create Account")}
                        </Button>
                    </StyledWrapper>
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(SignInCreateNewAccount)),
    definition: {
        allowedContexts: [SignInPageContext],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.content,
                displayName: "Main Text For New Customer",
                editorTemplate: "RichTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Contextual",
            },
            {
                name: fields.guestContent,
                displayName: "Main Text For Guest Checkout",
                editorTemplate: "RichTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
